import { useCallback, useEffect, useRef, useState } from 'react';
import { useApiWithAuth } from '@/hooks/api';
import {
  createMilestone,
  deleteMilestone,
  loadMilestones,
  loadProfileMilestones,
  updateMilestone,
} from './actions';
import { useMilestonesStore } from '.';
import { useActiveMilestones, useUpcomingMilestones } from './computed';
import type { Milestone, MilestoneCreate } from './types';
import { useInferredContext } from '../access/access';

export function useMilestones() {
  const { api, auth } = useApiWithAuth();
  const { state, dispatch } = useMilestonesStore();
  const ctx = useInferredContext();

  const load = useCallback(async () => {
    if (state.loaded) {
      return;
    }

    const criteria = {
      campusIds: Array.from(ctx?.campusIds || []),
      groupIds: Array.from(ctx?.groupIds || []),
    };

    if (!criteria.campusIds.length && !criteria.groupIds.length) {
      return;
    }

    try {
      const data = await loadMilestones({ api, auth, criteria });
      dispatch({ type: 'setMilestones', data });
    } catch (error) {
      console.error('Failed to load milestones:', error);
    }
  }, [api, auth, ctx, state.loaded, dispatch]);

  useEffect(() => {
    load();
  }, [load]);

  const active = useActiveMilestones({ state, dispatch });
  const upcoming = useUpcomingMilestones({ state, dispatch });

  return {
    loading: !state.loaded,
    active,
    upcoming,
    excluded: ctx?.excluded,
  };
}

export function useProfileMilestones(profileId: string) {
  const { api, auth } = useApiWithAuth();
  const [data, setData] = useState<Milestone[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const loadingRef = useRef(false);

  const load = useCallback(async () => {
    if (loaded || loadingRef.current) {
      return;
    }

    setLoading(true);
    loadingRef.current = true;

    try {
      const data = await loadProfileMilestones({ api, auth, profileId });
      setData(data);
      setLoaded(true);
      setLoading(false);
    } catch (e) {
      console.error(e);
    }
  }, [
    api,
    auth,
    profileId,
    loadingRef,
    loaded,
    setData,
    setLoaded,
    setLoading,
  ]);

  const addMilestone = (item: Milestone) => {
    setData([...data, item]);
  };

  const updateMilestone = (item: Milestone) => {
    debugger;
    const index = data.findIndex((m) => m.id === item.id);

    if (index === -1) {
      return false;
    }

    setData([...data.slice(0, index), item, ...data.slice(index + 1)]);

    return true;
  };

  const removeMilestone = (item: Milestone) => {
    const index = data.findIndex((m) => m.id === item.id);

    if (index === -1) {
      return false;
    }

    setData([...data.slice(0, index), ...data.slice(index + 1)]);

    return true;
  };

  useEffect(() => {
    load().then().catch(console.error);
  }, [load]);

  return {
    milestones: data,
    loaded,
    loading,
    addMilestone,
    updateMilestone,
    removeMilestone,
  };
}

export function useCreateMilestone() {
  const { api, auth } = useApiWithAuth();
  const [creating, setCreating] = useState(false);
  const { dispatch } = useMilestonesStore();

  const handleCreate = async (milestone: MilestoneCreate) => {
    setCreating(true);
    try {
      const result = await createMilestone({ api, auth, milestone });
      dispatch({ type: 'addMilestone', data: result.data });

      return result.data;
    } finally {
      setCreating(false);
    }
  };

  return {
    createMilestone: handleCreate,
    creating,
  };
}

export function useUpdateMilestone() {
  const { api, auth } = useApiWithAuth();
  const [updating, setUpdating] = useState(false);

  const handleUpdate = async (milestone: Milestone) => {
    setUpdating(true);
    try {
      const result = await updateMilestone({ api, auth, milestone });

      return result.data;
    } finally {
      setUpdating(false);
    }
  };

  return {
    updateMilestone: handleUpdate,
    updating,
  };
}

export function useDeleteMilestone() {
  const { api, auth } = useApiWithAuth();
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async (item: Milestone) => {
    setDeleting(true);
    try {
      await deleteMilestone({ api, auth, milestoneId: item.id });
    } finally {
      setDeleting(false);
    }
  };

  return {
    deleteMilestone: handleDelete,
    deleting,
  };
}
